// Phase MHM-5.3/5.4 · Micro hub lead pages — Dashboard, Profile, Orders, Inventory, Tickets.
// All READ-only. 8-second client timeout, error card with Retry, skeleton loader.
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Package, Clock, CheckCircle2, AlertTriangle, AlertOctagon, RefreshCw, Loader2, Truck, Users, Plus, X } from "lucide-react";
import api from "../../services/api";
import RoleGate from "../../components/RoleGate";
import MicroLeadSubNav from "../../components/microlead/MicroLeadSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import HubHandoverVerify from "../../components/hub/HubHandoverVerify";
import HubOpsPanel from "../../components/hub/HubOpsPanel";

const ALLOWED = ["micro_hub_lead", "main_hub_manager", "admin", "founder", "ceo"];

function Skeleton({ className = "" }) {
  return <div className={"animate-pulse bg-slate-100 rounded " + className} />;
}

function ErrorCard({ msg, onRetry }) {
  return (
    <div className="border border-rose-200 bg-rose-50 rounded-xl p-5" data-testid="microlead-error-card">
      <div className="flex items-start gap-3">
        <AlertOctagon className="h-5 w-5 text-rose-600 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold text-rose-800">Couldn't load</h4>
          <p className="text-sm text-rose-700 mt-1">{msg}</p>
          <div className="flex gap-2 mt-3">
            <button onClick={onRetry} data-testid="microlead-retry"
                    className="text-sm px-3 py-1.5 border border-rose-300 rounded-lg hover:bg-rose-100">Retry</button>
            <a href="/dashboard" className="text-sm px-3 py-1.5 border border-rose-300 rounded-lg hover:bg-rose-100">Back to dashboard</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Generic fetch helper — 8s timeout + clean errors.
async function timedGet(url, timeoutMs = 8000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const { data } = await api.get(url, { signal: ctrl.signal });
    return { data, error: null };
  } catch (e) {
    const code = e?.response?.status;
    const msg = code === 403 ? "Outside your micro hub scope."
              : code === 404 ? "Resource not found."
              : e?.name === "CanceledError" || e?.code === "ERR_CANCELED" ? "Request timed out after 8 seconds."
              : e.userMessage || e?.response?.data?.detail || "Network error.";
    return { data: null, error: { code, msg } };
  } finally { clearTimeout(t); }
}

function PendingBanner({ status, reason }) {
  if (status === "approved" || !status) return null;
  const isPending = status === "pending_admin_approval";
  return (
    <div className={`border rounded-xl p-3 text-sm ${isPending
        ? "bg-amber-50 border-amber-200 text-amber-800"
        : "bg-rose-50 border-rose-200 text-rose-800"}`}
         data-testid="microlead-pending-banner">
      {isPending
        ? "Your micro hub is awaiting admin approval. Operations are read-only until approved."
        : <>This micro hub was rejected by admin{reason ? `: "${reason}"` : "."}{" "}Contact your main hub manager.</>}
    </div>
  );
}

// ── Dashboard landing ─────────────────────────────────────────────
function DashboardBody() {
  const [k, setK] = useState(null);
  const [parcels, setParcels] = useState([]);
  const [inv, setInv] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [hubStatus, setHubStatus] = useState(null);
  const [rejReason, setRejReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [kRes, pRes, iRes, aRes, profRes] = await Promise.all([
      timedGet("/micro-hub/dashboard/kpis"),
      timedGet("/micro-hub/dashboard/incoming-parcels?limit=10"),
      timedGet("/micro-hub/inventory"),
      timedGet("/micro-hub/sla-alerts?limit=10"),
      timedGet("/micro-hub/profile"),
    ]);
    if (kRes.error) { setError(kRes.error); setLoading(false); return; }
    setK(kRes.data);
    setParcels(pRes.data?.rows || []);
    setInv((iRes.data?.rows || []).filter((r) => (r.current_stock || 0) <= (r.reorder_point || 0)).slice(0, 8));
    setAlerts(aRes.data?.rows || []);
    setHubStatus(profRes.data?.micro_hub?.approval_status || "approved");
    setRejReason(profRes.data?.micro_hub?.rejected_reason);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorCard msg={error.msg} onRetry={load}/>;
  // MHM-5.4 — 5 clickable KPI cards (todays pickups / pending / in-transit / delivered / workers)
  const cards = [
    { key: "orders_today",    label: "Today's pickups", Icon: Package,       val: k?.orders_today,
      to: "/dashboard/micro-hub/orders?filter=today_pickups",
      aria: "View today's pickups" },
    { key: "pending_pickups", label: "Pending",         Icon: Clock,         val: k?.pending_pickups,
      to: "/dashboard/micro-hub/orders?filter=pending",
      aria: "View pending pickups" },
    { key: "in_transit",      label: "In-Transit",      Icon: Truck,         val: k?.in_transit,
      to: "/dashboard/micro-hub/orders?filter=in_transit",
      aria: "View in-transit orders" },
    { key: "delivered",       label: "Delivered",       Icon: CheckCircle2,  val: k?.delivered,
      to: "/dashboard/micro-hub/orders?filter=delivered",
      aria: "View delivered orders" },
    { key: "workers_active",  label: "Workers",         Icon: Users,         val: k?.workers_active,
      to: "/dashboard/micro-hub/workers",
      aria: "Manage micro hub workers" },
  ];
  return (
    <div className="space-y-4" data-testid="microlead-dashboard">
      <PendingBanner status={hubStatus} reason={rejReason}/>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-500">
          Quick links: tap any KPI card to drill into the orders list, or jump to your workers roster.
        </div>
        <button
          onClick={() => navigate("/dashboard/micro-hub/workers?new=1")}
          data-testid="microlead-dashboard-add-worker"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B00] focus:ring-offset-1"
        >
          <Plus className="h-4 w-4"/> Add Worker
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="microlead-kpi-grid">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => navigate(c.to)}
            aria-label={`${c.label}, ${c.val ?? 0}. ${c.aria}`}
            data-testid={`microlead-kpi-${c.key}`}
            className="text-left bg-white border border-slate-200 rounded-2xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-[#FF6B00] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] focus-visible:ring-offset-1 cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 uppercase">{c.label}</span>
              <c.Icon className="h-4 w-4 text-[#FF6B00]"/>
            </div>
            {loading ? <Skeleton className="h-7 w-16 mt-2"/>
                     : <div className="text-2xl font-bold mt-1 font-['Outfit']">{c.val ?? 0}</div>}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="microlead-incoming-parcels">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
            Incoming parcels · {parcels.length}
          </h3>
          {loading ? <Skeleton className="h-40 w-full"/> :
            parcels.length === 0 ? <p className="text-sm text-slate-400 italic py-4">No active parcels.</p> :
            <ul className="divide-y divide-slate-100 text-sm">
              {parcels.map((p) => (
                <li key={p.tracking_id} className="py-2 flex justify-between gap-2">
                  <div>
                    <TrackingIdLink tid={p.tracking_id} className="text-xs"/>
                    <div className="text-xs text-slate-500">{p.user_name || "—"} → {p.drop_city || "—"}</div>
                  </div>
                  <span className="text-xs text-slate-500 capitalize">{(p.status || "").replace(/_/g, " ")}</span>
                </li>
              ))}
            </ul>}
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="microlead-low-stock">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
            Low-stock items · {inv.length}
          </h3>
          {loading ? <Skeleton className="h-40 w-full"/> :
            inv.length === 0 ? <p className="text-sm text-slate-400 italic py-4">All items above reorder point.</p> :
            <ul className="divide-y divide-slate-100 text-sm">
              {inv.map((i) => (
                <li key={i.item_id} className="py-2 flex justify-between">
                  <span>{i.name} <span className="text-xs text-slate-400">({i.sku})</span></span>
                  <span className="text-xs text-rose-600 font-semibold">
                    {i.current_stock || 0}/{i.reorder_point || 0} {i.unit}
                  </span>
                </li>
              ))}
            </ul>}
        </div>
      </div>
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="microlead-sla-alerts">
          <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider mb-2">
            Active SLA alerts · {alerts.length}
          </h3>
          <ul className="text-sm text-amber-900 space-y-1">
            {alerts.slice(0, 5).map((a, i) => (
              <li key={i}>{a.message || a.type}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// ── Profile ───────────────────────────────────────────────────────
function ProfileBody() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await timedGet("/micro-hub/profile");
    setD(data); setError(error); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error) return <ErrorCard msg={error.msg} onRetry={load}/>;
  if (loading) return <div className="space-y-3"><Skeleton className="h-32 w-full"/><Skeleton className="h-24 w-full"/></div>;
  const h = d?.micro_hub || {}; const p = d?.parent_main_hub || {}; const l = d?.lead || {};
  const perf = d?.performance || {};
  return (
    <div className="space-y-4" data-testid="microlead-profile">
      <PendingBanner status={h.approval_status} reason={h.rejected_reason}/>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Micro hub</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 text-sm">
          <div><dt className="text-xs text-slate-500">Name</dt><dd>{h.name || "—"}</dd></div>
          <div><dt className="text-xs text-slate-500">Hub ID</dt><dd className="font-mono">{h.hub_id || "—"}</dd></div>
          <div><dt className="text-xs text-slate-500">City</dt><dd>{h.city || "—"}</dd></div>
          <div><dt className="text-xs text-slate-500">State</dt><dd>{h.state || "—"}</dd></div>
          <div className="sm:col-span-2"><dt className="text-xs text-slate-500">Address</dt><dd>{h.address || "—"}</dd></div>
          <div><dt className="text-xs text-slate-500">Pincodes</dt>
               <dd>{(h.supported_pincodes || h.area_pincodes || []).join(", ") || "—"}</dd></div>
          <div><dt className="text-xs text-slate-500">Capacity / day</dt>
               <dd>{h.capacity_parcels_per_day || h.capacity || "—"}</dd></div>
          <div><dt className="text-xs text-slate-500">Approval status</dt>
               <dd className="font-semibold text-[#C2410C]">{h.approval_status || "approved"}</dd></div>
          <div><dt className="text-xs text-slate-500">Is active</dt><dd>{h.is_active ? "Yes" : "No"}</dd></div>
        </dl>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Parent main hub · {p.hub_id}</h3>
        <p className="text-sm">{p.name || "—"} · {p.city}, {p.state}</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Lead profile</h3>
        <p className="text-sm">{l.name || "—"} · <span className="font-mono text-xs">{l.email || "—"}</span></p>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Today's performance</h3>
        <div className="text-sm">Orders: <b>{perf.orders_today ?? 0}</b> / capacity {perf.capacity ?? "—"} ·
          utilization <b className="text-[#FF6B00]">{perf.utilization_pct ?? 0}%</b></div>
      </div>
    </div>
  );
}


// ── Orders ────────────────────────────────────────────────────────
const FILTER_LABELS = {
  today_pickups: "Today's pickups",
  pending: "Pending",
  in_transit: "In-Transit",
  delivered: "Delivered",
};

function OrdersBody() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "";

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (status) q.set("status", status);
    if (filter) q.set("filter", filter);
    const { data, error } = await timedGet(`/micro-hub/orders?${q}`);
    setRows(data?.rows || []); setError(error); setLoading(false);
  }, [search, status, filter]);
  useEffect(() => { load(); }, [load]);

  const clearFilter = () => {
    const next = new URLSearchParams(params);
    next.delete("filter");
    setParams(next, { replace: true });
  };

  if (error) return <ErrorCard msg={error.msg} onRetry={load}/>;
  return (
    <div className="space-y-3" data-testid="microlead-orders">
      {filter && FILTER_LABELS[filter] && (
        <div className="flex items-center gap-2" data-testid="microlead-orders-filter-chip">
          <span className="text-xs uppercase tracking-wider text-slate-500">Filter:</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-50 border border-orange-200 text-[#C2410C] rounded-full text-sm font-medium">
            {FILTER_LABELS[filter]}
            <button
              onClick={clearFilter}
              aria-label="Clear filter"
              data-testid="microlead-orders-filter-clear"
              className="hover:bg-orange-100 rounded-full p-0.5">
              <X className="h-3.5 w-3.5"/>
            </button>
          </span>
          <span className="text-xs text-slate-500">· {rows.length} order{rows.length === 1 ? "" : "s"}</span>
        </div>
      )}
      <div className="flex gap-2 items-center">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="microlead-orders-search"
               placeholder="Search tracking_id or name"
               className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1"/>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="microlead-orders-status"
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="assigned">Assigned</option>
          <option value="picked_up">Picked up</option>
          <option value="in_transit">In transit</option>
          <option value="delivered">Delivered</option>
        </select>
        <button onClick={load} className="text-sm px-3 py-1.5 border border-slate-200 rounded-lg">
          {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCw className="h-4 w-4"/>}
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">Tracking</th><th>Status</th><th>From → To</th>
                <th>Weight</th><th>Created</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-400">No orders</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.tracking_id} data-testid={`microlead-orders-row-${r.tracking_id}`}>
                <td className="px-3 py-2 text-xs">
                  <TrackingIdLink tid={r.tracking_id}/>
                </td>
                <td className="px-3 py-2 text-xs capitalize">{(r.status || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-xs">{r.user_name || "—"} → {r.drop_city || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.package_weight || 0} kg</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Inventory (read-only) ─────────────────────────────────────────
function InventoryBody() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await timedGet("/micro-hub/inventory");
    setRows(data?.rows || []); setError(error); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error) return <ErrorCard msg={error.msg} onRetry={load}/>;
  return (
    <div data-testid="microlead-inventory">
      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded p-2 mb-3"
           data-testid="microlead-inventory-readonly-banner">
        Read-only view · Stock movements require main hub manager access.
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">SKU</th><th>Name</th>
                <th>Current</th><th>Reorder at</th><th>Unit</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-400">No inventory items</td></tr>
            )}
            {rows.map((i) => {
              const low = (i.current_stock || 0) <= (i.reorder_point || 0);
              return (
                <tr key={i.item_id} data-testid={`microlead-inventory-row-${i.item_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{i.sku}</td>
                  <td className="px-3 py-2">{i.name}</td>
                  <td className={`px-3 py-2 ${low ? "text-rose-600 font-semibold" : ""}`}>{i.current_stock || 0}</td>
                  <td className="px-3 py-2">{i.reorder_point || 0}</td>
                  <td className="px-3 py-2 text-xs">{i.unit || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Tickets (read-only) ───────────────────────────────────────────
function TicketsBody() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await timedGet("/micro-hub/tickets");
    setRows(data?.rows || []); setError(error); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error) return <ErrorCard msg={error.msg} onRetry={load}/>;
  return (
    <div data-testid="microlead-tickets">
      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded p-2 mb-3">
        Read-only view · Tickets are managed by your main hub manager.
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">Ticket</th><th>Subject</th>
                <th>Priority</th><th>Status</th><th>Created</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-400">No tickets</td></tr>
            )}
            {rows.map((t) => (
              <tr key={t.ticket_id}>
                <td className="px-3 py-2 font-mono text-xs">{t.ticket_id}</td>
                <td className="px-3 py-2">{t.subject}</td>
                <td className="px-3 py-2 text-xs capitalize">{t.priority}</td>
                <td className="px-3 py-2 text-xs capitalize">{t.status}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{t.created_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Shell ─────────────────────────────────────────────────────────
function Shell({ title, testid, children }) {
  return (
    <RoleGate allow={ALLOWED}>
      <MicroLeadSubNav/>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid={testid}>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-slate-900"
            data-testid={`${testid}-title`}>{title}</h1>
        {children}
      </div>
    </RoleGate>
  );
}

export function MicroHubLeadDashboard() {
  return <Shell title="Micro hub overview" testid="microlead-dashboard-page"><HubHandoverVerify/><HubOpsPanel/><DashboardBody/></Shell>;
}
export function MicroHubLeadProfile() {
  return <Shell title="My micro hub" testid="microlead-profile-page"><ProfileBody/></Shell>;
}
export function MicroHubLeadOrders() {
  return <Shell title="Hub orders" testid="microlead-orders-page"><OrdersBody/></Shell>;
}
export function MicroHubLeadInventory() {
  return <Shell title="Inventory" testid="microlead-inventory-page"><InventoryBody/></Shell>;
}
export function MicroHubLeadTickets() {
  return <Shell title="Tickets" testid="microlead-tickets-page"><TicketsBody/></Shell>;
}

// MHM-5.4 — 404 fallback for `/dashboard/micro-hub/orders/:tid` deep-links.
// The unified detail surface is the drawer (opened via TrackingIdLink); this URL
// pattern is intentionally not a routable page and renders an "Order not found" card.
function OrderNotFoundBody() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center"
         data-testid="microlead-order-not-found">
      <AlertOctagon className="h-10 w-10 text-rose-500 mx-auto mb-3"/>
      <h2 className="text-xl font-semibold mb-1 font-['Outfit']">Order not found</h2>
      <p className="text-sm text-slate-500 mb-4">
        We couldn't find that tracking ID, or you don't have access to it.
        Order details open via the tracking-ID link inside the orders list.
      </p>
      <Link to="/dashboard/micro-hub/orders"
            data-testid="microlead-order-not-found-back"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg text-sm">
        ← Back to orders
      </Link>
    </div>
  );
}
export function MicroHubOrderNotFound() {
  return <Shell title="Order detail" testid="microlead-order-not-found-page"><OrderNotFoundBody/></Shell>;
}
